import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, Shield, Flag, AlertTriangle, RotateCw, ChevronRight, Filter, Globe2 } from "lucide-react";
import { api } from "../lib/api";
import { Flag as CountryFlag } from "../components/Flag";

const CONTAINER = "mx-auto max-w-[1700px] px-4 sm:px-8 lg:px-12";

// Taxonomy mapped to EXACT backend competition_type values (audited, not inferred from labels).
// DOMESTIC_LEAGUE / DOMESTIC_CUP / CONTINENTAL_CLUB / CONTINENTAL_NATIONS / INTERNATIONAL_FRIENDLY.
export const TYPE_META = {
  DOMESTIC_LEAGUE: { cat: "leagues", label: "Domestic League", scope: "DOMESTIC", Icon: Trophy },
  DOMESTIC_CUP: { cat: "cups", label: "Domestic Cup", scope: "DOMESTIC", Icon: Crown },
  CONTINENTAL_CLUB: { cat: "continental", label: "Continental Club", scope: "CONTINENTAL", Icon: Shield },
  CONTINENTAL_NATIONS: { cat: "national", label: "Continental · National Teams", scope: "NATIONAL", Icon: Flag },
  INTERNATIONAL_FRIENDLY: { cat: "national", label: "International · National Teams", scope: "NATIONAL", Icon: Flag },
};
const FALLBACK_META = { cat: "other", label: "Competition", scope: "DOMESTIC", Icon: Trophy };
export const metaOf = (t) => TYPE_META[t] || FALLBACK_META;

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "leagues", label: "Domestic Leagues" },
  { key: "cups", label: "Domestic Cups" },
  { key: "continental", label: "Continental" },
  { key: "national", label: "National Teams" },
];

const SCOPE_ACCENT = {
  DOMESTIC: "#E5B842",
  CONTINENTAL: "#38bdf8",
  NATIONAL: "#34d399",
};

export const STATUS_META = {
  PLANNED: { label: "Planned", cls: "bg-slate-900/70 text-slate-300 border-slate-600/50", dot: "bg-slate-400" },
  ACTIVE: { label: "Active", cls: "bg-emerald-950/70 text-emerald-400 border-emerald-500/40", dot: "bg-emerald-400" },
  IN_PROGRESS: { label: "In Progress", cls: "bg-[#140f02]/70 text-[#F0C72A] border-[#E5B31E]/50", dot: "bg-[#F0C72A]" },
  COMPLETED: { label: "Completed", cls: "bg-slate-900/60 text-slate-400 border-slate-700/50", dot: "bg-slate-600" },
};

export function CompStatus({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-900/70 text-slate-300 border-slate-600/50", dot: "bg-slate-500" };
  const live = status === "IN_PROGRESS";
  return (
    <span data-testid={`comp-status-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider border ${m.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot} ${live ? "animate-pulse" : ""}`} />
      {m.label}
    </span>
  );
}

function Skeleton() {
  return <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] p-6 animate-pulse h-44" />;
}

function CompCard({ c }) {
  const m = metaOf(c.competition_type);
  const Icon = m.Icon;
  const accent = SCOPE_ACCENT[m.scope];
  return (
    <Link
      to={`/competition/${c.id}`}
      data-testid={`competition-card-${c.id}`}
      className="group relative rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-6 hover:border-[#E5B842]/55 flex flex-col overflow-hidden"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 30px -12px rgba(212,175,55,0.35)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} aria-hidden="true" />
      <div className="flex items-center justify-between">
        <div className="w-11 h-11 rounded-lg bg-[#161616] border flex items-center justify-center" style={{ borderColor: `${accent}55` }}>
          <Icon className="w-6 h-6" style={{ color: accent }} />
        </div>
        <CompStatus status={c.status} />
      </div>
      <div className="mt-4 text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: accent }}>{m.label}</div>
      <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mt-1 leading-tight">{c.name}</h3>
      <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-mono text-slate-400">
        <span data-testid={`competition-territory-${c.id}`} className="px-2 py-0.5 rounded bg-[#161616] border border-[#2b2512] inline-flex items-center gap-1.5 text-slate-200">
          {c.country ? <CountryFlag country={c.country} width={18} /> : <Globe2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
          {c.country ? c.country.name : "International"}
        </span>
        <span className="px-2 py-0.5 rounded bg-[#161616] border border-[#2b2512]">{c.discipline}</span>
        {typeof c.team_count === "number" && c.team_count > 0 && <span className="px-2 py-0.5 rounded bg-[#161616] border border-[#2b2512]">{c.team_count} teams</span>}
        {c.season && <span className="px-2 py-0.5 rounded bg-[#161616] border border-[#2b2512]">Season {c.season.name}</span>}
      </div>
      <div className="mt-auto pt-4 flex items-center justify-end text-[11px] font-mono text-slate-500">
        View <ChevronRight className="w-4 h-4 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }} />
      </div>
    </Link>
  );
}

function SelectFilter({ testid, label, value, onChange, options }) {
  if (options.length <= 1) return null;
  return (
    <label className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-slate-500">
      {label}
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2 text-slate-100 text-xs focus:outline-none focus:border-[#E5B842]/70"
        style={{ transition: "border-color 0.15s ease" }}
      >
        <option value="all">All</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export default function CompetitionsList() {
  const [comps, setComps] = useState(null);
  const [error, setError] = useState(false);
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [season, setSeason] = useState("all");
  const [country, setCountry] = useState("all");

  const load = useCallback(() => {
    setComps(null); setError(false);
    api.get("/competitions").then(({ data }) => setComps(data.competitions)).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const { statusOpts, seasonOpts, countryOpts } = useMemo(() => {
    const list = comps || [];
    const uniq = (arr) => Array.from(new Map(arr.map((x) => [x.value, x])).values());
    return {
      statusOpts: uniq(list.map((c) => ({ value: c.status, label: STATUS_META[c.status]?.label || c.status }))),
      seasonOpts: uniq(list.filter((c) => c.season).map((c) => ({ value: c.season.name, label: `Season ${c.season.name}` }))),
      countryOpts: uniq(list.map((c) => ({ value: c.country?.iso3 || "INT", label: c.country ? c.country.name : "International" }))),
    };
  }, [comps]);

  const filtered = (comps || []).filter((c) => {
    if (cat !== "all" && metaOf(c.competition_type).cat !== cat) return false;
    if (status !== "all" && c.status !== status) return false;
    if (season !== "all" && c.season?.name !== season) return false;
    if (country !== "all" && (c.country?.iso3 || "INT") !== country) return false;
    return true;
  });

  return (
    <div className="animate-fade-up pb-16">
      {/* Compact cinematic hero (~280px) with a subtle gold pyramid motif (CSS only, no imagery). */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 150% at 50% 0%, rgba(212,175,55,0.16) 0%, transparent 56%)" }} />
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 pointer-events-none opacity-[0.10]" aria-hidden="true"
          style={{ width: 520, height: 180, background: "conic-gradient(from 180deg at 50% 100%, transparent 68deg, #E5B842 90deg, transparent 112deg)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-14`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">WSTF Competitions</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">The Global Pyramid</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-2xl">Domestic leagues, cups, continental tiers and national-team competitions — the WSTF pyramid in action.</p>
        </div>
      </section>

      {/* Primary taxonomy tabs */}
      <div className={`${CONTAINER} pt-8`}>
        <div className="inline-flex flex-wrap gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]" data-testid="competitions-categories">
          {CATEGORIES.map((o) => (
            <button key={o.key} data-testid={`comp-cat-${o.key}`} onClick={() => setCat(o.key)}
              className={`px-4 py-2 rounded-lg text-xs sm:text-sm font-heading font-bold uppercase tracking-wide whitespace-nowrap ${cat === o.key ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
              style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{o.label}</button>
          ))}
        </div>
      </div>

      {/* Secondary filters — only rendered when backed by >1 real value */}
      {comps && (statusOpts.length > 1 || seasonOpts.length > 1 || countryOpts.length > 1) && (
        <div className={`${CONTAINER} pt-5`}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="competitions-filters">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#E5B842]"><Filter className="w-3.5 h-3.5" /> Filters</span>
            <SelectFilter testid="comp-filter-status" label="Status" value={status} onChange={setStatus} options={statusOpts} />
            <SelectFilter testid="comp-filter-season" label="Season" value={season} onChange={setSeason} options={seasonOpts} />
            <SelectFilter testid="comp-filter-country" label="Country" value={country} onChange={setCountry} options={countryOpts} />
          </div>
        </div>
      )}

      <div className={`${CONTAINER} pt-8`}>
        {error ? (
          <div data-testid="competitions-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
            <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
            <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load competitions</div>
            <button onClick={load} data-testid="competitions-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : !comps ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="competitions-skeleton">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="competitions-empty" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
            No competitions match these filters
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="competitions-list">
            {filtered.map((c) => <CompCard key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
